import AgricultureIcon from "@mui/icons-material/Agriculture";
import PersonIcon from "@mui/icons-material/Person";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import differenceBy from "lodash/differenceBy";
import { useCallback, useEffect, useMemo } from "react";
import type { FieldValues } from "react-hook-form";
import { useFormContext, useWatch } from "react-hook-form";
import { TransitionGroup } from "react-transition-group";
import { useManagers } from "~api/managers.ts";
import { useTeams } from "~api/teams";
import { AddButton } from "~components/shared/Buttons/AddButton";
import { Divider } from "~components/shared/Divider/Divider";
import { useForeignTenantToolWarning } from "~components/shared/ForeignTenantToolWarning/ForeignTenantToolWarningContext.tsx";
import { ControlledTextField } from "~components/shared/Form/ControlledTextField.tsx";
import { ControlledItemMultiSelector } from "~components/shared/Form/ItemSelector/ItemMultiSelector";
import { ControlledItemSelector } from "~components/shared/Form/ItemSelector/ItemSelector";
import { SelectedItem } from "~components/shared/Form/ItemSelector/SelectedItem";
import { ContentSkeleton } from "~components/shared/Loading/ContentSkeleton/ContentSkeleton";
import { LazyLoadResourceName } from "~components/shared/ResourceName/ResourceName";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import { useResources } from "~contexts/ResourceContext/ResourceContext.tsx";
import type { Employee, Team } from "~generated";
import type { Resource } from "~lib/resourceHelpers.ts";
import { getResourceName, ResourceType } from "~lib/resourceHelpers.ts";

import styles from "./TeamForm.module.scss";

export interface TeamFormData extends FieldValues {
  resources: Resource[];
  foreman: Employee;
  constructionSiteManager: Employee;
  name: Team["name"];
}

export interface TeamFormProps {
  team?: Team;
  additionalResources?: Resource[];
  onFormReady: (isReady: boolean) => void;
}

export function TeamForm({
  team,
  additionalResources,
  onFormReady,
}: TeamFormProps) {
  const { activeResources: resources, isLoading: areResourcesLoading } =
    useResources();
  const {
    data: constructionSiteManagers,
    isLoading: areConstructionSiteManagersLoading,
  } = useManagers();
  const { data: teams, isLoading: areTeamsLoading } = useTeams();
  const t = useTranslation();
  const checkForeignTenantTool = useForeignTenantToolWarning();

  const { control, setValue } = useFormContext<TeamFormData>();
  const selected = useWatch({
    control,
    name: "resources",
    defaultValue: [] as Resource[],
  });

  const beforeResourcesChange = useCallback(
    (newItems: Resource[], proceed: () => void) => {
      const newItem = newItems.find(
        (item) => !selected.some((r) => r.id === item.id),
      );
      if (newItem) {
        checkForeignTenantTool(newItem, proceed);
      } else {
        proceed();
      }
    },
    [selected, checkForeignTenantTool],
  );

  const unselectedAdditionalResources: Resource[] = useMemo(() => {
    if (!additionalResources) {
      return [];
    }
    return differenceBy(additionalResources, selected, "id");
  }, [selected, additionalResources]);

  const addResource = useCallback(
    (resource: Resource) => {
      setValue("resources", selected.concat(resource), {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [selected, setValue],
  );
  const addUnselectedResources = useCallback(() => {
    setValue("resources", selected.concat(unselectedAdditionalResources), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [selected, setValue, unselectedAdditionalResources]);

  const getOptionIcon = ({ type }: Resource) => {
    if (type === ResourceType.EMPLOYEE) {
      return <PersonIcon fontSize="small" />;
    }
    return <AgricultureIcon fontSize="small" />;
  };

  const isLoading =
    areResourcesLoading ||
    areTeamsLoading ||
    areConstructionSiteManagersLoading;

  useEffect(() => {
    onFormReady(!isLoading);
  }, [onFormReady, isLoading]);

  return (
    <>
      {isLoading ? (
        <ContentSkeleton contentType="dialog" />
      ) : (
        <Stack direction="column" spacing={2}>
          <ControlledTextField
            name="name"
            control={control}
            rules={{
              required: { value: true, message: t.required },
              validate: (n) =>
                n.trim() === team?.name ||
                !teams?.find(({ name }) => name === n.trim()) ||
                t["name has already been taken"],
            }}
            autoFocus
            label={t.teamFormName}
            color="secondary"
          />

          <ControlledItemSelector
            name="constructionSiteManager"
            control={control}
            options={constructionSiteManagers || []}
            rules={{ required: true }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={getResourceName}
            getOptionId={({ id }) => id as string}
            textFieldProps={{ color: "secondary" }}
            labels={{
              noOptions: t.noOptions,
              label: t.teamConstructionSiteManagerLabel,
              error: t.teamConstructionSiteManagerError,
            }}
          />

          <ControlledItemMultiSelector<TeamFormData, "resources", Resource>
            control={control}
            name="resources"
            checkableFieldName="foreman"
            rules={{ validate: (v) => Boolean(v) && v.length > 0 }}
            checkableRules={{ required: true }}
            beforeOnChange={beforeResourcesChange}
            options={resources}
            color="secondary"
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={getResourceName}
            getOptionId={({ type, id }) => `${type}-${id}`}
            getOptionIcon={getOptionIcon}
            isOptionCheckable={(option) =>
              option.type === ResourceType.EMPLOYEE
            }
            labels={{
              noOptions: t.noOptions,
              label: t.teamFormItemMultiSelectorLabel,
              error: t.teamFormItemMultiSelectorError,
              removeResource: t.removeResource,
              remove: t.remove,
              selectedItemsHeadline: t.selectForeman,
              checkedError: t.foremanRequired,
              open: t.open,
              close: t.close,
              clear: t.clear,
              loading: t.loading,
            }}
          />

          {additionalResources && additionalResources.length > 0 && (
            <>
              <Divider
                strokeWidth={1}
                strokeDashSize={2}
                strokeGap={4}
                className={styles.divider}
              />
              <div>
                <Stack spacing={0}>
                  <Grid container sx={{ justifyContent: "space-between" }}>
                    <Grid>
                      <Typography variant={"caption"}>
                        {t.teamFormAddPermanently}
                      </Typography>
                    </Grid>
                    <Grid>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => addUnselectedResources()}
                      >
                        {t.teamFormAddAll}
                      </Button>
                    </Grid>
                  </Grid>
                  {unselectedAdditionalResources.length === 0 && (
                    <Typography
                      variant={"caption"}
                      sx={{ fontStyle: "italic", color: "gray" }}
                    >
                      {t.teamFormAllAdded}
                    </Typography>
                  )}
                  <TransitionGroup>
                    {unselectedAdditionalResources.map((resource) => (
                      <Collapse key={resource.id}>
                        <SelectedItem
                          color="secondary"
                          actions={
                            <AddButton
                              title={resource.displayName}
                              onClick={() =>
                                checkForeignTenantTool(resource, () =>
                                  addResource(resource),
                                )
                              }
                            />
                          }
                        >
                          <LazyLoadResourceName resourceId={resource.id} />
                        </SelectedItem>
                      </Collapse>
                    ))}
                  </TransitionGroup>
                </Stack>
              </div>
            </>
          )}
        </Stack>
      )}
    </>
  );
}
